// PATH: src/app/pages/raajneeti/currentAffairs/howTheySee/index.jsx
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { getHowTheySee } from "api/InternalApis/Currentaffairs";
import "./howTheySee.scss";

// ---------------------------------------------------------------------------
// How Would They See It — POC's view D (#ca-hwt): date archive chips +
// news card with three thinker takes. Data: GET /get-howtheysee
// (data: [{ date, news: [{ Title, ShortDescription, thinkers: [...] }] }])
// ---------------------------------------------------------------------------

function getInitials(name) {
  if (!name) return "??";
  return (
    String(name)
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("") || "??"
  );
}

// "2026-10-02" -> "02 OCT 2026"
function formatDateLabel(dateStr) {
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d
    .toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    .toUpperCase()
    .replace(/,/g, "");
}

const HowWouldTheySeeIt = () => {
  const [status, setStatus] = useState("loading"); // loading | success | empty | error
  const [days, setDays] = useState([]); // [{ date, news: [...] }]
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStatus("loading");
      try {
        const res = await getHowTheySee({});
        const list = (res?.data ?? []).filter((d) => d?.date);
        const sorted = [...list].sort((a, b) => new Date(b.date) - new Date(a.date));

        if (cancelled) return;

        setDays(sorted);
        setActiveIdx(0);
        setStatus(sorted.length ? "success" : "empty");
      } catch (error) {
        console.error("Error fetching How Would They See It:", error);
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const activeDay = useMemo(() => days[activeIdx] ?? null, [days, activeIdx]);

  const handleShare = async (newsItem) => {
    const shareText = `${newsItem.Title}\n\n${newsItem.ShortDescription ?? ""}`.trim();
    try {
      if (navigator.share) {
        await navigator.share({ title: newsItem.Title, text: shareText });
      } else {
        await navigator.clipboard.writeText(shareText);
        toast.success("Copied to clipboard");
      }
    } catch (error) {
      if (error?.name !== "AbortError") console.error("Error sharing card:", error);
    }
  };

  const noNews = status === "empty" || !activeDay || (activeDay.news ?? []).length === 0;

  return (
    <div className="as-wrap">
      <div style={{ fontSize: 12, color: "var(--ink3)", margin: "2px 0 14px" }}>
        Understand today&rsquo;s news through the lens of thinkers and former Indian personalities.
      </div>

      {status === "success" && days.length > 0 && (
        <div className="hwt-archive-bar" id="hwt-archive-bar">
          {days.map((d, i) => (
            <button
              key={d.date}
              type="button"
              className={`hwt-archive-chip${i === activeIdx ? " on" : ""}`}
              onClick={() => setActiveIdx(i)}
            >
              {formatDateLabel(d.date)}
            </button>
          ))}
        </div>
      )}

      <div id="disp-hwt-body">
        {status === "error" ? (
          <div className="hwt-status err">
            Couldn&rsquo;t load How Would They See It. Please check your connection and try again.
          </div>
        ) : status === "loading" ? (
          <div className="hwt-status">Loading how thinkers see today&rsquo;s news&hellip;</div>
        ) : noNews ? (
          <div className="hwt-status">
            No thinker takes are live for this date yet. Check back soon...
          </div>
        ) : (
          activeDay.news.map((newsItem, newsIdx) => (
            <div className="hwt-card" key={newsItem.NewsID ?? newsIdx}>
              <div className="hwt-date-row">
                <div className="hwt-date">
                  {formatDateLabel(newsItem.Date ?? activeDay.date)}
                  {activeDay.news.length > 1 ? ` \u00b7 NEWS ${newsIdx + 1} OF ${activeDay.news.length}` : ""}
                </div>
                {!!newsItem.total_approved_thinkers && (
                  <div className="hwt-flagship">
                    <i className="ti ti-users" /> {newsItem.total_approved_thinkers} thinkers
                  </div>
                )}
              </div>

              <div className="hwt-headline">&ldquo;{newsItem.Title}&rdquo;</div>
              {newsItem.ShortDescription && <div className="hwt-hook">{newsItem.ShortDescription}</div>}

              {(newsItem.thinkers ?? []).length > 0 && (
                <div className="hwt-grid">
                  {newsItem.thinkers.map((thinker, tIdx) => (
                    <div className="hwt-thinker" key={thinker.id ?? tIdx}>
                      <div className="hwt-th-top">
                        <div className="hwt-avatar">{getInitials(thinker.thinker_name)}</div>
                        <div>
                          <div className="hwt-name">{thinker.thinker_name}</div>
                          {thinker.tradition && <div className="hwt-tag">{thinker.tradition}</div>}
                        </div>
                      </div>

                      {thinker.framework && <div className="hwt-framework">{thinker.framework}</div>}

                      {thinker.analysis && <div className="hwt-take">{thinker.analysis}</div>}

                      {thinker.exam_tip && (
                        <div className="hwt-examtip">
                          <i className="ti ti-bulb" /> {thinker.exam_tip}
                        </div>
                      )}

                      {(thinker.topic || thinker.paper) && (
                        <div className="hwt-open">
                          {thinker.topic && <span>{thinker.topic}</span>}
                          {thinker.paper && <span>{thinker.paper}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* <div className="hwt-share-row">
                <button type="button" className="hwt-share-btn" onClick={() => handleShare(newsItem)}>
                  <i className="ti ti-share" /> Share this card
                </button>
              </div> */}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default HowWouldTheySeeIt;